/**
 * Terrain Theme System Type Definitions
 *
 * This file contains all type definitions for the Battlefield Terrain Visualization system,
 * including theme management, parallax backgrounds, particle systems, and grid effects.
 */

// =============================================================================
// Parallax Background System Types
// =============================================================================

/**
 * Configuration for a single parallax layer
 */
export interface ParallaxLayerConfig {
  /** Asset URL or placeholder key for the layer texture */
  textureUrl: string;

  /** Parallax factor: 0 = static, 1 = moves 1:1 with camera */
  parallaxFactor: number;

  /** Base scale for the layer */
  scale: number;

  /** Layer opacity (0-1) */
  alpha: number;

  /** Optional texture tiling scale */
  tileScale?: { x: number; y: number };

  /** Whether to use a single sprite (false) or tiling sprite (true). Default: true */
  useTiling?: boolean;

  /** Pixel offset applied after centering (horizontal). Use background position editor to find values. */
  offsetX?: number;

  /** Pixel offset applied after centering (vertical). Use background position editor to find values. */
  offsetY?: number;
}

/**
 * Configuration for the three-layer parallax background system
 */
export interface ParallaxBackgroundConfig {
  /** Three parallax layers (far, mid, near) */
  layers: {
    far: ParallaxLayerConfig;
    mid: ParallaxLayerConfig;
    near: ParallaxLayerConfig;
  };

  /** Bleed factor: 1.5 = 50% extra beyond grid */
  bleedFactor: number;

  /** Grid bounds for calculating terrain size */
  gridBounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

/**
 * Camera state for parallax calculations
 */
export interface CameraState {
  /** Camera center X position */
  x: number;

  /** Camera center Y position */
  y: number;

  /** Zoom factor (1 = default) */
  zoom: number;
}

// =============================================================================
// Particle System Types
// =============================================================================

/**
 * Base configuration for particle systems
 */
export interface ParticleSystemConfig {
  /** Maximum number of particles in the system */
  maxParticles: number;

  /** Texture URL or placeholder texture */
  texture: string;

  /** Particle tint color (hex) */
  tint: number;

  /** Particle velocity range (pixels per second) */
  speed: { min: number; max: number };

  /** Movement direction range (radians) */
  direction: { min: number; max: number };

  /** Particle scale range */
  scale: { min: number; max: number };

  /** Particle alpha/opacity range (0-1) */
  alpha: { min: number; max: number };

  /** Spawn area bounds */
  bounds: { x: number; y: number; width: number; height: number };

  /** Whether particles wrap around at edges */
  wrapEdges: boolean;

  /** Whether particles fade out at edges */
  fadeAtEdges: boolean;

  /** Whether particles respawn at bottom when reaching top */
  respawnAtBottom: boolean;
}

/**
 * Configuration for ground fog particle system
 * Fog particles drift slowly and wrap at edges
 */
export interface FogConfig extends ParticleSystemConfig {
  wrapEdges: true;
  fadeAtEdges: false;
  respawnAtBottom: false;
}

/**
 * Configuration for floating dust particle system
 * Dust particles drift upward with horizontal sway, fade at top, and respawn at bottom
 */
export interface DustConfig extends ParticleSystemConfig {
  wrapEdges: false;
  fadeAtEdges: true;
  respawnAtBottom: true;

  /** Horizontal sway amplitude (pixels) */
  swayAmplitude: number;

  /** Sway frequency (oscillations per second) */
  swayFrequency: number;
}

// =============================================================================
// Grid Effects Types
// =============================================================================

/**
 * Configuration for Tron-style grid effects
 */
export interface GridEffectsConfig {
  /** Glow color for grid lines (hex) */
  glowColor: number;

  /** Glow intensity (0-1) */
  glowIntensity: number;

  /** Speed of glow pulse animation */
  glowPulseSpeed: number;

  /** Amount of intensity variation in pulse (0-0.5) */
  glowPulseAmount: number;

  /** Duration for hex materialization animation (ms) */
  materializeSpeed: number;

  /** Duration for creature spawn effect (ms) */
  spawnEffectDuration: number;

  /** Duration for grid fade-out at combat start (ms) */
  fadeOutDuration: number;
}

/**
 * State for a hex materialization effect
 */
export interface HexMaterializationState {
  /** Hex coordinate key (e.g., "q0r0") */
  hexKey: string;

  /** Animation progress (0-1) */
  progress: number;

  /** Whether effect is currently active (hovering) */
  isActive: boolean;

  /** Timestamp when effect started */
  startTime: number;
}

/**
 * State for a creature spawn effect
 */
export interface SpawnEffectState {
  /** Unit ID being spawned */
  unitId: string;

  /** Hex coordinate key where unit is spawning */
  hexKey: string;

  /** Animation progress (0-1) */
  progress: number;

  /** Timestamp when effect started */
  startTime: number;
}

/**
 * Battle phase enumeration for controlling grid visibility
 */
export enum BattlePhase {
  DEPLOYMENT = 'deployment',
  COUNTDOWN = 'countdown',
  COMBAT = 'combat',
  FINISHED = 'finished',
}

// =============================================================================
// Theme Management Types
// =============================================================================

/**
 * Complete terrain theme configuration
 * Defines all visual aspects of the battlefield including backgrounds, particles, and effects
 */
export interface TerrainTheme {
  /** Unique theme identifier */
  themeId: string;

  /** Display name for the theme */
  name: string;

  /** User-facing description of the theme */
  description: string;

  /** URL for theme preview thumbnail */
  thumbnail: string;

  /** Asset URLs for all theme textures */
  assets: {
    /** Far background layer texture */
    farBackground: string;

    /** Mid background layer texture */
    midBackground: string;

    /** Near background (floor) texture - should be tileable */
    nearBackground: string;

    /** Fog particle texture */
    fogParticle: string;

    /** Dust particle texture */
    dustParticle: string;

    /** Optional custom grid glow texture */
    gridGlowTexture?: string;
  };

  /** Theme color palette */
  colors: {
    /** Grid glow color for Tron effects (hex) */
    gridGlow: number;

    /** Fog particle tint color (hex) */
    fogTint: number;

    /** Dust particle tint color (hex) */
    dustTint: number;

    /** Overall scene ambient light color (hex) */
    ambientLight: number;
  };

  /** Parallax layer settings */
  parallax: {
    far: { factor: number; scale: number };
    mid: { factor: number; scale: number };
    near: { factor: number; scale: number; offsetX?: number; offsetY?: number };
  };

  /** Particle system settings */
  particles: {
    fog: {
      /** Particles per 1000x1000 px area */
      density: number;

      /** Velocity range (pixels/second) */
      speed: { min: number; max: number };

      /** Scale range */
      scale: { min: number; max: number };

      /** Alpha range (0-1) */
      alpha: { min: number; max: number };
    };
    dust: {
      /** Particles per 1000x1000 px area */
      density: number;

      /** Velocity range (pixels/second) */
      speed: { min: number; max: number };

      /** Scale range */
      scale: { min: number; max: number };

      /** Alpha range (0-1) */
      alpha: { min: number; max: number };

      /** Horizontal sway amplitude (pixels) */
      swayAmplitude: number;

      /** Sway frequency (oscillations/second) */
      swayFrequency: number;
    };
  };

  /** Grid effect settings */
  gridEffects: {
    /** Glow intensity (0-1) */
    glowIntensity: number;

    /** Pulse animation speed */
    glowPulseSpeed: number;

    /** Pulse intensity variation (0-0.5) */
    glowPulseAmount: number;

    /** Hex materialization duration (ms) */
    materializeSpeed: number;

    /** Spawn effect duration (ms) */
    spawnEffectDuration: number;
  };
}

/**
 * Theme loading state tracker
 */
export interface ThemeLoadState {
  /** Theme being loaded */
  themeId: string;

  /** Current load status */
  status: 'pending' | 'loading' | 'loaded' | 'error';

  /** Load progress (0-1) */
  progress: number;

  /** Error message if status is 'error' */
  error?: string;

  /** Loaded textures (only present when status is 'loaded') */
  textures?: Record<string, unknown>; // PIXI.Texture in actual use
}
